import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { LIGHT_COLOR_PRESETS } from './products/_shared/lightColors'

type ObjectKind = 'light' | 'decoration'

interface SceneDebugObject {
  uuid: string
  name: string
  type: string
  kind: ObjectKind
  position: [number, number, number]
  target?: [number, number, number]
  intensity?: number
  color?: string
  angle?: number
  scale?: [number, number, number]
  visible?: boolean
  penumbra?: number
  distance?: number
  decay?: number
  castShadow?: boolean
  shadowRadius?: number
  shadowBias?: number
  shadowNormalBias?: number
  shadowMapSize?: number
}

const round = (n: number) => Math.round(n * 100) / 100

const isLight = (obj: THREE.Object3D): obj is THREE.Light =>
  (obj as THREE.Light).isLight === true

const isDecorationRoot = (obj: THREE.Object3D): boolean => {
  const ud = obj.userData as Record<string, unknown> | undefined
  return !!ud && (ud.isRoom === true || ud.isWazeWithFlowers === true)
}

const objectHasTarget = (
  obj: THREE.Object3D
): obj is THREE.Object3D & { target: THREE.Object3D } => {
  const candidate = (obj as unknown as { target?: unknown }).target
  return candidate instanceof THREE.Object3D
}

const colorToHex = (c: THREE.Color | THREE.ColorRepresentation): string => {
  const col =
    c instanceof THREE.Color ? c : new THREE.Color(c as THREE.ColorRepresentation)
  return `#${col.getHexString()}`
}

type AddableLightType = 'ambient' | 'directional' | 'point' | 'spot'

const DEBUG_USER_DATA_KEY = '__sceneDebugAdded'

function exportLightsAsSpec(scene: THREE.Scene): unknown[] {
  const specs: unknown[] = []
  const worldPos = new THREE.Vector3()
  const worldTarget = new THREE.Vector3()
  const pickDefined = <T extends object>(obj: T): T => {
    const out = {} as T
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined) (out as Record<string, unknown>)[k] = v
    }
    return out
  }

  scene.traverse((obj) => {
    if (!isLight(obj)) return
    obj.getWorldPosition(worldPos)
    const pos: [number, number, number] = [
      round(worldPos.x),
      round(worldPos.y),
      round(worldPos.z),
    ]
    const color = `#${(obj.color as THREE.Color).getHexString()}`

    if (obj instanceof THREE.AmbientLight) {
      specs.push({ type: 'ambient', intensity: round(obj.intensity), color })
      return
    }
    if (obj instanceof THREE.DirectionalLight) {
      obj.target.getWorldPosition(worldTarget)
      specs.push(
        pickDefined({
          type: 'directional',
          position: pos,
          target: [
            round(worldTarget.x),
            round(worldTarget.y),
            round(worldTarget.z),
          ] as [number, number, number],
          intensity: round(obj.intensity),
          color,
          castShadow: obj.castShadow || undefined,
        })
      )
      return
    }
    if (obj instanceof THREE.SpotLight) {
      obj.target.getWorldPosition(worldTarget)
      specs.push(
        pickDefined({
          type: 'spot',
          position: pos,
          target: [
            round(worldTarget.x),
            round(worldTarget.y),
            round(worldTarget.z),
          ] as [number, number, number],
          intensity: round(obj.intensity),
          angle: round(obj.angle),
          penumbra: round(obj.penumbra),
          distance: obj.distance || undefined,
          // ProductLights renders a missing decay as 0, so only omit when 0
          decay: obj.decay !== 0 ? round(obj.decay) : undefined,
          color,
          castShadow: obj.castShadow || undefined,
          shadowRadius: obj.castShadow ? round(obj.shadow.radius) : undefined,
        })
      )
      return
    }
    if (obj instanceof THREE.PointLight) {
      specs.push(
        pickDefined({
          type: 'point',
          position: pos,
          intensity: round(obj.intensity),
          distance: obj.distance || undefined,
          decay: obj.decay !== 0 ? round(obj.decay) : undefined,
          color,
          castShadow: obj.castShadow || undefined,
        })
      )
    }
  })
  return specs
}

interface CameraSnapshot {
  position: [number, number, number]
  target: [number, number, number]
}

function snapshotCamera(
  camera: THREE.Camera | null,
  controlsTarget: THREE.Vector3 | null
): CameraSnapshot | null {
  if (!camera) return null
  const pos: [number, number, number] = [
    round(camera.position.x),
    round(camera.position.y),
    round(camera.position.z),
  ]
  let target: [number, number, number]
  if (controlsTarget) {
    target = [
      round(controlsTarget.x),
      round(controlsTarget.y),
      round(controlsTarget.z),
    ]
  } else {
    const dir = new THREE.Vector3()
    camera.getWorldDirection(dir)
    const dist = camera.position.length() || 1000
    const t = camera.position.clone().add(dir.multiplyScalar(dist))
    target = [round(t.x), round(t.y), round(t.z)]
  }
  return { position: pos, target }
}

function exportDecorations(
  scene: THREE.Scene
): Array<{
  marker: string
  position: [number, number, number]
  scale: [number, number, number]
  rotation: [number, number, number]
}> {
  const out: ReturnType<typeof exportDecorations> = []
  scene.traverse((obj) => {
    if (!isDecorationRoot(obj)) return
    const ud = obj.userData as Record<string, unknown>
    const marker =
      ud.isRoom === true
        ? 'isRoom'
        : ud.isWazeWithFlowers === true
          ? 'isWazeWithFlowers'
          : 'unknown'
    const wp = new THREE.Vector3()
    obj.getWorldPosition(wp)
    out.push({
      marker,
      position: [round(wp.x), round(wp.y), round(wp.z)],
      scale: [round(obj.scale.x), round(obj.scale.y), round(obj.scale.z)],
      rotation: [
        round(obj.rotation.x),
        round(obj.rotation.y),
        round(obj.rotation.z),
      ],
    })
  })
  return out
}

function detectProductFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const m = window.location.pathname.match(
    /\/(wardrobe|wardrobe-new-collection|bedside|tv-stand|rack|stand|table|work-table|office-table)(?:\/|$)/
  )
  return m ? m[1] : null
}

function buildConfigDump(
  scene: THREE.Scene,
  camera: THREE.Camera | null,
  controlsTarget: THREE.Vector3 | null
): string {
  const lights = exportLightsAsSpec(scene)
  const cam = snapshotCamera(camera, controlsTarget)
  const decorations = exportDecorations(scene)

  const payload = {
    $schema: 'dulap-scene-debug/v1',
    instructions:
      'Paste this whole block to the assistant. It will update products/<product>/Lights.tsx (lights), products/<product>/viewer.ts (cameraPosition + target), and products/<product>/decorations.tsx (decoration positions/scale/rotation) for the product below.',
    product: detectProductFromUrl(),
    timestamp: new Date().toISOString(),
    lights,
    camera: cam,
    decorations,
  }
  return JSON.stringify(payload, null, 2)
}

function logFullConfig(
  scene: THREE.Scene,
  camera: THREE.Camera | null,
  controlsTarget: THREE.Vector3 | null
): void {
  const dump = buildConfigDump(scene, camera, controlsTarget)
   
  console.log(
    '%c[scene-debug] full config',
    'color:#0f0;font-weight:bold',
    `\n\n${dump}`
  )
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard
      .writeText(dump)
       
      .then(() => console.log('[scene-debug] copied to clipboard'))
      .catch(() => {})
  }
}

function createLight(type: AddableLightType): THREE.Light {
  switch (type) {
    case 'ambient':
      return new THREE.AmbientLight(0xffffff, 0.3)
    case 'directional': {
      const l = new THREE.DirectionalLight(0xffffff, 1)
      l.position.set(500, 800, 500)
      return l
    }
    case 'point': {
      // decay 0 / distance 0: scene is in cm, physical falloff (1/d) kills the
      // light at this scale; matches the rig convention in products/*/Lights.tsx
      const l = new THREE.PointLight(0xffffff, 1, 0, 0)
      l.position.set(0, 500, 0)
      return l
    }
    case 'spot': {
      const l = new THREE.SpotLight(0xffffff, 1, 0, Math.PI / 6, 0.3, 0)
      l.position.set(500, 800, 500)
      return l
    }
  }
}

function serialize(obj: THREE.Object3D, kind: ObjectKind): SceneDebugObject {
  const worldPos = new THREE.Vector3()
  obj.getWorldPosition(worldPos)
  const entry: SceneDebugObject = {
    uuid: obj.uuid,
    name: obj.name || '(unnamed)',
    type: obj.type,
    kind,
    position: [round(worldPos.x), round(worldPos.y), round(worldPos.z)],
    scale: [round(obj.scale.x), round(obj.scale.y), round(obj.scale.z)],
  }
  if (objectHasTarget(obj)) {
    const t = new THREE.Vector3()
    obj.target.getWorldPosition(t)
    entry.target = [round(t.x), round(t.y), round(t.z)]
  }
  if (isLight(obj)) {
    const light = obj as THREE.Light & { color: THREE.Color }
    entry.intensity = round(light.intensity)
    entry.color = colorToHex(light.color)
    entry.visible = obj.visible

    const readShadow = (l: THREE.Light & { shadow: THREE.LightShadow }) => {
      entry.castShadow = l.castShadow
      entry.shadowRadius = round(l.shadow.radius)
      entry.shadowBias = l.shadow.bias
      entry.shadowNormalBias = l.shadow.normalBias
      entry.shadowMapSize = l.shadow.mapSize.x
    }

    if (obj instanceof THREE.SpotLight) {
      entry.angle = round(obj.angle)
      entry.penumbra = round(obj.penumbra)
      entry.distance = round(obj.distance)
      entry.decay = round(obj.decay)
      readShadow(obj)
    } else if (obj instanceof THREE.DirectionalLight) {
      readShadow(obj)
    } else if (obj instanceof THREE.PointLight) {
      entry.distance = round(obj.distance)
      entry.decay = round(obj.decay)
      readShadow(obj)
    }
  }
  return entry
}

function collectSceneObjects(scene: THREE.Scene): SceneDebugObject[] {
  const list: SceneDebugObject[] = []
  const walk = (obj: THREE.Object3D) => {
    if (isLight(obj)) {
      list.push(serialize(obj, 'light'))
      return
    }
    if (isDecorationRoot(obj)) {
      list.push(serialize(obj, 'decoration'))
      return
    }
    for (const child of obj.children) walk(child)
  }
  walk(scene)
  return list
}

const isDebugQuery = (): boolean => {
  if (typeof window === 'undefined') return false
  const v = new URLSearchParams(window.location.search).get('debug')
  return v === 'true' || v === '1'
}

interface CameraInfo {
  position: [number, number, number]
  target: [number, number, number]
  hasControls: boolean
}

interface FpsInfo {
  fps: number
  worstFrameMs: number
}

interface CollectorProps {
  onCollect: (objects: SceneDebugObject[]) => void
  onCameraSnapshot: (info: CameraInfo) => void
  onFpsSample: (info: FpsInfo) => void
  selectedUuid: string | null
  onSceneReady: (
    scene: THREE.Scene,
    camera: THREE.Camera,
    controls: { target: THREE.Vector3; update?: () => void } | null
  ) => void
  intervalMs?: number
}

// Mirrors FpsReporter's gap heuristic: demand-mode pauses are idle, not slow.
const DEBUG_FPS_IDLE_GAP_MS = 100
const DEBUG_FPS_WINDOW = 30
const DEBUG_FPS_PUSH_INTERVAL_MS = 250

// Max spot lights for which we draw a position→target path segment.
const DEBUG_MAX_SPOT_PATHS = 64
const _spotPathFrom = new THREE.Vector3()
const _spotPathTo = new THREE.Vector3()

const SceneDebugCollector: React.FC<CollectorProps> = ({
  onCollect,
  onCameraSnapshot,
  onFpsSample,
  selectedUuid,
  onSceneReady,
  intervalMs = 500,
}) => {
  const { scene, camera, controls } = useThree()
  const onCollectRef = useRef(onCollect)
  onCollectRef.current = onCollect
  const onCameraSnapshotRef = useRef(onCameraSnapshot)
  onCameraSnapshotRef.current = onCameraSnapshot
  const onSceneReadyRef = useRef(onSceneReady)
  onSceneReadyRef.current = onSceneReady
  const onFpsSampleRef = useRef(onFpsSample)
  onFpsSampleRef.current = onFpsSample

  const fpsBufRef = useRef<number[]>([])
  const lastFrameAtRef = useRef<number>(0)
  const lastFpsPushRef = useRef<number>(0)

  useEffect(() => {
    const ctrl =
      controls && (controls as unknown as { target?: THREE.Vector3 }).target
        ? (controls as unknown as {
            target: THREE.Vector3
            update?: () => void
          })
        : null
    onSceneReadyRef.current(scene, camera, ctrl)
    onCollectRef.current(collectSceneObjects(scene))

    const pushCameraSnapshot = () => {
      const snap = snapshotCamera(camera, ctrl?.target ?? null)
      if (!snap) return
      onCameraSnapshotRef.current({
        position: snap.position,
        target: snap.target,
        hasControls: !!ctrl,
      })
    }
    pushCameraSnapshot()

    const id = window.setInterval(() => {
      onCollectRef.current(collectSceneObjects(scene))
      pushCameraSnapshot()
    }, intervalMs)
    return () => window.clearInterval(id)
  }, [scene, camera, controls, intervalMs])

  const highlightRef = useRef<THREE.Mesh>(null)
  const cameraTargetRef = useRef<THREE.Mesh>(null)
  const spotPathRef = useRef<THREE.LineSegments>(null)
  const spotPathPos = useMemo(
    () => new Float32Array(DEBUG_MAX_SPOT_PATHS * 2 * 3),
    []
  )

  useFrame(() => {
    const now = performance.now()
    const prev = lastFrameAtRef.current
    lastFrameAtRef.current = now
    if (prev !== 0) {
      const dt = now - prev
      if (dt > 0 && dt <= DEBUG_FPS_IDLE_GAP_MS) {
        const buf = fpsBufRef.current
        buf.push(dt)
        if (buf.length > DEBUG_FPS_WINDOW) buf.shift()
        if (
          buf.length >= 10 &&
          now - lastFpsPushRef.current >= DEBUG_FPS_PUSH_INTERVAL_MS
        ) {
          lastFpsPushRef.current = now
          let sum = 0
          let worst = 0
          for (const v of buf) {
            sum += v
            if (v > worst) worst = v
          }
          const avgMs = sum / buf.length
          onFpsSampleRef.current({ fps: 1000 / avgMs, worstFrameMs: worst })
        }
      } else {
        fpsBufRef.current = []
      }
    }

    const mesh = highlightRef.current
    if (mesh) {
      if (!selectedUuid) {
        mesh.visible = false
      } else {
        const target = scene.getObjectByProperty('uuid', selectedUuid)
        if (!target) {
          mesh.visible = false
        } else {
          mesh.visible = true
          target.getWorldPosition(mesh.position)
        }
      }
    }

    const camTargetMesh = cameraTargetRef.current
    if (camTargetMesh) {
      const ctrl = (controls as unknown as { target?: THREE.Vector3 }) ?? null
      if (ctrl?.target) {
        camTargetMesh.visible = true
        camTargetMesh.position.copy(ctrl.target)
      } else {
        camTargetMesh.visible = false
      }
    }

    const spotPath = spotPathRef.current
    if (spotPath) {
      const arr = spotPathPos
      let count = 0
      scene.traverse((o) => {
        if (count >= DEBUG_MAX_SPOT_PATHS) return
        if (!(o as THREE.SpotLight).isSpotLight) return
        const spot = o as THREE.SpotLight
        spot.getWorldPosition(_spotPathFrom)
        spot.target.getWorldPosition(_spotPathTo)
        const base = count * 6
        arr[base] = _spotPathFrom.x
        arr[base + 1] = _spotPathFrom.y
        arr[base + 2] = _spotPathFrom.z
        arr[base + 3] = _spotPathTo.x
        arr[base + 4] = _spotPathTo.y
        arr[base + 5] = _spotPathTo.z
        count++
      })
      const attr = spotPath.geometry.getAttribute('position') as
        | THREE.BufferAttribute
        | undefined
      if (attr) attr.needsUpdate = true
      spotPath.geometry.setDrawRange(0, count * 2)
      spotPath.visible = count > 0
    }
  })

  return (
    <>
      <mesh ref={highlightRef} renderOrder={9999} visible={false}>
        <sphereGeometry args={[40, 16, 16]} />
        <meshBasicMaterial
          color="#ff00ff"
          wireframe
          depthTest={false}
          depthWrite={false}
          transparent
        />
      </mesh>
      <mesh ref={cameraTargetRef} renderOrder={9999} visible={false}>
        <boxGeometry args={[30, 30, 30]} />
        <meshBasicMaterial
          color="#00ffff"
          wireframe
          depthTest={false}
          depthWrite={false}
          transparent
        />
      </mesh>
      <lineSegments
        ref={spotPathRef}
        renderOrder={9999}
        frustumCulled={false}
        visible={false}
      >
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[spotPathPos, 3]}
          />
        </bufferGeometry>
        <lineBasicMaterial
          color="#ffaa00"
          depthTest={false}
          transparent
          opacity={0.9}
        />
      </lineSegments>
    </>
  )
}

interface NumberFieldProps {
  label: string
  value: number
  step?: number
  min?: number
  max?: number
  onChange: (v: number) => void
}

const NumberField: React.FC<NumberFieldProps> = ({
  label,
  value,
  step = 1,
  min,
  max,
  onChange,
}) => {
  const apply = (raw: string) => {
    const n = parseFloat(raw)
    if (!Number.isNaN(n)) onChange(n)
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: '#9f9', minWidth: 14 }}>{label}</span>
        <input
          type="number"
          step={step}
          value={value}
          onChange={(e) => apply(e.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            color: '#0f0',
            border: '1px solid #044',
            padding: '1px 4px',
            font: 'inherit',
          }}
        />
      </label>
      {min !== undefined && max !== undefined && (
        <input
          type="range"
          step={step}
          min={min}
          max={max}
          value={value}
          onChange={(e) => apply(e.target.value)}
          style={{ width: '100%', accentColor: '#0f0' }}
        />
      )}
    </div>
  )
}

const CheckboxRow: React.FC<{
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}> = ({ label, checked, onChange }) => (
  <label
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginTop: 4,
      cursor: 'pointer',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{ accentColor: '#0f0' }}
    />
    <span style={{ color: '#9f9' }}>{label}</span>
  </label>
)

const Vec3Row: React.FC<{
  label: string
  value: [number, number, number]
  step?: number
  min?: number
  max?: number
  onChange: (v: [number, number, number]) => void
}> = ({ label, value, step, min, max, onChange }) => (
  <div style={{ marginTop: 4 }}>
    <div style={{ color: '#9f9' }}>{label}</div>
    <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
      <NumberField
        label="x"
        value={value[0]}
        step={step}
        min={min}
        max={max}
        onChange={(v) => onChange([v, value[1], value[2]])}
      />
      <NumberField
        label="y"
        value={value[1]}
        step={step}
        min={min}
        max={max}
        onChange={(v) => onChange([value[0], v, value[2]])}
      />
      <NumberField
        label="z"
        value={value[2]}
        step={step}
        min={min}
        max={max}
        onChange={(v) => onChange([value[0], value[1], v])}
      />
    </div>
  </div>
)

interface EditPanelProps {
  object: SceneDebugObject
  sceneRef: React.MutableRefObject<THREE.Scene | null>
  onMutated: () => void
  onRemove: (uuid: string) => void
}

const EditPanel: React.FC<EditPanelProps> = ({
  object,
  sceneRef,
  onMutated,
  onRemove,
}) => {
  const getObj = useCallback((): THREE.Object3D | null => {
    const scene = sceneRef.current
    if (!scene) return null
    return scene.getObjectByProperty('uuid', object.uuid) ?? null
  }, [object.uuid, sceneRef])

  const setLocalPosition = (v: [number, number, number]) => {
    const o = getObj()
    if (!o) return
    if (o.parent) {
      const worldToLocal = new THREE.Matrix4().copy(o.parent.matrixWorld).invert()
      const local = new THREE.Vector3(...v).applyMatrix4(worldToLocal)
      o.position.copy(local)
    } else {
      o.position.set(...v)
    }
    o.updateMatrixWorld(true)
    onMutated()
  }

  const setTarget = (v: [number, number, number]) => {
    const o = getObj()
    if (!o || !objectHasTarget(o)) return
    const parent = o.target.parent
    if (parent) {
      const worldToLocal = new THREE.Matrix4().copy(parent.matrixWorld).invert()
      const local = new THREE.Vector3(...v).applyMatrix4(worldToLocal)
      o.target.position.copy(local)
    } else {
      o.target.position.set(...v)
    }
    o.target.updateMatrixWorld(true)
    onMutated()
  }

  const setIntensity = (n: number) => {
    const o = getObj()
    if (!o || !isLight(o)) return
    o.intensity = n
    onMutated()
  }

  const setColor = (hex: string) => {
    const o = getObj()
    if (!o || !isLight(o)) return
    o.color.set(hex)
    onMutated()
  }

  const setAngle = (n: number) => {
    const o = getObj() as (THREE.SpotLight & THREE.Object3D) | null
    if (!o || !isLight(o)) return
    if (typeof (o as THREE.SpotLight).angle === 'number') {
      ;(o as THREE.SpotLight).angle = n
      onMutated()
    }
  }

  const setScale = (v: [number, number, number]) => {
    const o = getObj()
    if (!o) return
    o.scale.set(...v)
    o.updateMatrixWorld(true)
    onMutated()
  }

  const setVisible = (b: boolean) => {
    const o = getObj()
    if (!o) return
    o.visible = b
    onMutated()
  }

  const setCastShadow = (b: boolean) => {
    const o = getObj()
    if (!o || !isLight(o)) return
    o.castShadow = b
    onMutated()
  }

  const setPenumbra = (n: number) => {
    const o = getObj() as THREE.SpotLight | null
    if (!o || !(o as THREE.SpotLight).isSpotLight) return
    o.penumbra = n
    onMutated()
  }

  const setDistance = (n: number) => {
    const o = getObj() as (THREE.SpotLight | THREE.PointLight) | null
    if (!o || typeof (o as { distance?: number }).distance !== 'number') return
    o.distance = n
    onMutated()
  }

  const setDecay = (n: number) => {
    const o = getObj() as (THREE.SpotLight | THREE.PointLight) | null
    if (!o || typeof (o as { decay?: number }).decay !== 'number') return
    o.decay = n
    onMutated()
  }

  // Returns the light only if it owns a shadow camera (spot/dir/point).
  const getShadowLight = (): (THREE.Light & {
    shadow: THREE.LightShadow
  }) | null => {
    const o = getObj()
    if (!o || !isLight(o)) return null
    const l = o as THREE.Light & { shadow?: THREE.LightShadow }
    return l.shadow ? (l as THREE.Light & { shadow: THREE.LightShadow }) : null
  }

  const setShadowRadius = (n: number) => {
    const l = getShadowLight()
    if (!l) return
    l.shadow.radius = n
    onMutated()
  }

  const setShadowBias = (n: number) => {
    const l = getShadowLight()
    if (!l) return
    l.shadow.bias = n
    onMutated()
  }

  const setShadowNormalBias = (n: number) => {
    const l = getShadowLight()
    if (!l) return
    l.shadow.normalBias = n
    onMutated()
  }

  const setShadowMapSize = (n: number) => {
    const l = getShadowLight()
    if (!l) return
    const size = Math.max(64, Math.round(n))
    l.shadow.mapSize.set(size, size)
    // Force the shadow map + camera to rebuild at the new resolution.
    l.shadow.map?.dispose()
    l.shadow.map = null as unknown as THREE.WebGLRenderTarget
    l.shadow.needsUpdate = true
    onMutated()
  }

  return (
    <div
      style={{
        borderTop: '1px solid #0f0',
        padding: 6,
        background: 'rgba(0,255,0,0.06)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 4,
        }}
      >
        <span style={{ color: '#ff9', flex: 1 }}>
          edit · {object.name} [{object.type}]
        </span>
        <button
          type="button"
          onClick={() => onRemove(object.uuid)}
          title="remove from scene"
          style={{
            background: 'transparent',
            color: '#f66',
            border: '1px solid #f66',
            padding: '1px 6px',
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          delete
        </button>
      </div>

      <Vec3Row
        label="position (world)"
        value={object.position}
        step={10}
        min={-3000}
        max={3000}
        onChange={setLocalPosition}
      />

      {object.target && (
        <Vec3Row
          label="target (world)"
          value={object.target}
          step={10}
          min={-3000}
          max={3000}
          onChange={setTarget}
        />
      )}

      {object.kind === 'decoration' && object.scale && (
        <Vec3Row
          label="scale"
          value={object.scale}
          step={0.05}
          min={0}
          max={300}
          onChange={setScale}
        />
      )}

      {object.kind === 'light' && (
        <>
          {typeof object.visible === 'boolean' && (
            <CheckboxRow
              label="visible"
              checked={object.visible}
              onChange={setVisible}
            />
          )}
          {typeof object.intensity === 'number' && (
            <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
              <NumberField
                label="intensity"
                value={object.intensity}
                step={0.1}
                min={0}
                max={20}
                onChange={setIntensity}
              />
            </div>
          )}
          {object.color && (
            <>
              <label
                style={{
                  marginTop: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span style={{ color: '#9f9' }}>color</span>
                <input
                  type="color"
                  value={object.color}
                  onChange={(e) => setColor(e.target.value)}
                  style={{
                    width: 40,
                    height: 22,
                    background: 'transparent',
                    border: '1px solid #044',
                    padding: 0,
                  }}
                />
                <span style={{ color: '#0f0' }}>{object.color}</span>
              </label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(13, 1fr)',
                  gap: 2,
                  marginTop: 4,
                }}
              >
                {LIGHT_COLOR_PRESETS.map((p) => {
                  const isActive =
                    object.color?.toLowerCase() === p.hex.toLowerCase()
                  return (
                    <button
                      key={p.hex}
                      type="button"
                      onClick={() => setColor(p.hex)}
                      title={`${p.label}${p.kelvin ? ` · ${p.kelvin}K` : ''} — ${p.description}`}
                      style={{
                        height: 16,
                        background: p.hex,
                        border: `1px solid ${isActive ? '#ff0' : '#044'}`,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    />
                  )
                })}
              </div>
            </>
          )}
          {typeof object.angle === 'number' && (
            <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
              <NumberField
                label="angle"
                value={object.angle}
                step={0.01}
                min={0}
                max={Math.PI / 2}
                onChange={setAngle}
              />
            </div>
          )}
          {typeof object.penumbra === 'number' && (
            <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
              <NumberField
                label="penumbra"
                value={object.penumbra}
                step={0.01}
                min={0}
                max={1}
                onChange={setPenumbra}
              />
            </div>
          )}
          {typeof object.distance === 'number' && (
            <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
              <NumberField
                label="distance"
                value={object.distance}
                step={10}
                min={0}
                max={10000}
                onChange={setDistance}
              />
            </div>
          )}
          {typeof object.decay === 'number' && (
            <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
              <NumberField
                label="decay"
                value={object.decay}
                step={0.1}
                min={0}
                max={4}
                onChange={setDecay}
              />
            </div>
          )}
          {typeof object.castShadow === 'boolean' && (
            <>
              <CheckboxRow
                label="castShadow"
                checked={object.castShadow}
                onChange={setCastShadow}
              />
              {object.castShadow && (
                <div
                  style={{
                    marginTop: 4,
                    padding: 4,
                    border: '1px solid #044',
                    borderRadius: 3,
                  }}
                >
                  <div style={{ color: '#9cf', marginBottom: 2 }}>shadow</div>
                  {typeof object.shadowMapSize === 'number' && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <NumberField
                        label="map"
                        value={object.shadowMapSize}
                        step={512}
                        min={256}
                        max={8192}
                        onChange={setShadowMapSize}
                      />
                    </div>
                  )}
                  {typeof object.shadowRadius === 'number' && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                      <NumberField
                        label="radius"
                        value={object.shadowRadius}
                        step={0.5}
                        min={0}
                        max={50}
                        onChange={setShadowRadius}
                      />
                    </div>
                  )}
                  {typeof object.shadowBias === 'number' && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                      <NumberField
                        label="bias"
                        value={object.shadowBias}
                        step={0.0001}
                        onChange={setShadowBias}
                      />
                    </div>
                  )}
                  {typeof object.shadowNormalBias === 'number' && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                      <NumberField
                        label="nBias"
                        value={object.shadowNormalBias}
                        step={0.01}
                        onChange={setShadowNormalBias}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

interface CameraSectionProps {
  camera: CameraInfo | null
  onPositionChange: (v: [number, number, number]) => void
  onTargetChange: (v: [number, number, number]) => void
}

const CameraSection: React.FC<CameraSectionProps> = ({
  camera,
  onPositionChange,
  onTargetChange,
}) => {
  const [open, setOpen] = useState(true)
  if (!camera) return null
  return (
    <div
      style={{
        borderTop: '1px solid #044',
        borderBottom: '1px solid #044',
        padding: 6,
        background: 'rgba(0,255,255,0.04)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ color: '#9cf', flex: 1 }}>
          camera {open ? '▾' : '▸'}
        </span>
        {!camera.hasControls && (
          <span style={{ color: '#f96', fontSize: 10 }}>(no orbit)</span>
        )}
      </div>
      <div style={{ color: '#cfc', marginTop: 2 }}>
        pos {camera.position.join(', ')}
      </div>
      <div style={{ color: '#ff9' }}>target {camera.target.join(', ')}</div>
      {open && (
        <>
          <Vec3Row
            label="camera position"
            value={camera.position}
            step={10}
            min={-5000}
            max={5000}
            onChange={onPositionChange}
          />
          <Vec3Row
            label="orbit target"
            value={camera.target}
            step={10}
            min={-3000}
            max={3000}
            onChange={onTargetChange}
          />
        </>
      )}
    </div>
  )
}

interface OverlayProps {
  objects: SceneDebugObject[]
  selectedUuid: string | null
  onSelect: (uuid: string | null) => void
  sceneRef: React.MutableRefObject<THREE.Scene | null>
  onMutated: () => void
  onAddLight: (type: AddableLightType) => void
  onRemove: (uuid: string) => void
  onLogLights: () => void
  cameraInfo: CameraInfo | null
  fpsInfo: FpsInfo | null
  onCameraPositionChange: (v: [number, number, number]) => void
  onCameraTargetChange: (v: [number, number, number]) => void
}

function fpsColor(fps: number): string {
  if (fps < 30) return '#f66'
  if (fps < 50) return '#ff9'
  return '#9f9'
}

const SceneDebugOverlay: React.FC<OverlayProps> = ({
  objects,
  selectedUuid,
  onSelect,
  sceneRef,
  onMutated,
  onAddLight,
  onRemove,
  onLogLights,
  cameraInfo,
  fpsInfo,
  onCameraPositionChange,
  onCameraTargetChange,
}) => {
  const [open, setOpen] = useState(true)
  const [filter, setFilter] = useState<'all' | 'light' | 'decoration'>('all')

  const filtered = useMemo(
    () => (filter === 'all' ? objects : objects.filter((o) => o.kind === filter)),
    [objects, filter]
  )

  const selected = useMemo(
    () => objects.find((o) => o.uuid === selectedUuid) ?? null,
    [objects, selectedUuid]
  )

  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 1000,
        width: 500,
        maxHeight: '100vh',
        background: 'rgba(0,0,0,0.82)',
        color: '#0f0',
        font: '11px ui-monospace,SFMono-Regular,Menlo,monospace',
        border: '1px solid #0f0',
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 6px',
          borderBottom: '1px solid #0f0',
          background: 'rgba(0,255,0,0.08)',
        }}
      >
        <strong style={{ flex: 1 }}>
          scene debug · {objects.length} ({objects.filter((o) => o.kind === 'light').length}L /{' '}
          {objects.filter((o) => o.kind === 'decoration').length}D)
        </strong>
        {fpsInfo && (
          <span
            title={`worst frame ${fpsInfo.worstFrameMs.toFixed(1)} ms`}
            style={{ color: fpsColor(fpsInfo.fps) }}
          >
            {Math.round(fpsInfo.fps)} fps
            <span style={{ color: '#888' }}>
              {' '}
              ({fpsInfo.worstFrameMs.toFixed(0)}ms)
            </span>
          </span>
        )}
        <button
          type="button"
          onClick={onLogLights}
          title="log full config (lights + camera + decorations) and copy to clipboard"
          style={{
            background: 'transparent',
            color: '#ff9',
            border: '1px solid #ff9',
            padding: '1px 6px',
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          copy config
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            background: 'transparent',
            color: '#0f0',
            border: '1px solid #0f0',
            padding: '1px 6px',
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          {open ? 'hide' : 'show'}
        </button>
      </div>

      {open && (
        <>
          <CameraSection
            camera={cameraInfo}
            onPositionChange={onCameraPositionChange}
            onTargetChange={onCameraTargetChange}
          />

          <div style={{ display: 'flex', gap: 4, padding: 6 }}>
            {(['all', 'light', 'decoration'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                style={{
                  flex: 1,
                  background: filter === k ? 'rgba(0,255,0,0.2)' : 'transparent',
                  color: '#0f0',
                  border: '1px solid #0f0',
                  padding: '2px 4px',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                {k}
              </button>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 4,
              padding: '0 6px 6px',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#9f9' }}>+light</span>
            {(['ambient', 'directional', 'point', 'spot'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => onAddLight(t)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  color: '#0f0',
                  border: '1px solid #044',
                  padding: '2px 4px',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                {t}
              </button>
            ))}
          </div>

          <div
            style={{
              overflow: 'auto',
              maxHeight: 240,
              padding: '0 6px 6px',
            }}
          >
            {filtered.map((o) => {
              const isSel = o.uuid === selectedUuid
              return (
                <div
                  key={o.uuid}
                  onClick={() => onSelect(isSel ? null : o.uuid)}
                  style={{
                    padding: '3px 4px',
                    margin: '2px 0',
                    border: `1px solid ${isSel ? '#ff0' : '#044'}`,
                    background: isSel ? 'rgba(255,255,0,0.08)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <span style={{ color: o.kind === 'light' ? '#ff9' : '#9cf' }}>
                      [{o.kind === 'light' ? 'L' : 'D'}]
                    </span>{' '}
                    <span style={{ color: '#9f9' }}>{o.name}</span>{' '}
                    <span style={{ color: '#6c6' }}>{o.type}</span>
                  </div>
                  <div style={{ color: '#cfc' }}>pos {o.position.join(', ')}</div>
                  {o.target && (
                    <div style={{ color: '#ff9' }}>target {o.target.join(', ')}</div>
                  )}
                </div>
              )
            })}
            {filtered.length === 0 && (
              <div style={{ color: '#999', padding: 4 }}>no matches</div>
            )}
          </div>

          {selected && (
            <EditPanel
              object={selected}
              sceneRef={sceneRef}
              onMutated={onMutated}
              onRemove={onRemove}
            />
          )}
        </>
      )}
    </div>
  )
}

export interface UseSceneDebugResult {
  enabled: boolean
  objects: SceneDebugObject[]
  selectedUuid: string | null
  /** Render inside <Canvas>. Collects scene objects + draws selection highlight. */
  canvasNode: ReactNode
  /** Render outside <Canvas>. List + edit panel. */
  overlayNode: ReactNode
}

/**
 * Enables a scene inspector when `?debug=true` (or `?debug=1`) is set.
 * Lists lights and decoration roots (userData.isRoom / .isWazeWithFlowers).
 * Click an entry to edit its world position, target, intensity, color, etc.
 */
export function useSceneDebug(intervalMs = 500): UseSceneDebugResult {
  const enabled = useMemo(() => isDebugQuery(), [])
  const [objects, setObjects] = useState<SceneDebugObject[]>([])
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const cameraRef = useRef<THREE.Camera | null>(null)
  const controlsRef = useRef<{
    target: THREE.Vector3
    update?: () => void
  } | null>(null)
  const [mutationTick, setMutationTick] = useState(0)
  const [cameraInfo, setCameraInfo] = useState<CameraInfo | null>(null)
  const [fpsInfo, setFpsInfo] = useState<FpsInfo | null>(null)

  const handleSceneReady = useCallback(
    (
      scene: THREE.Scene,
      camera: THREE.Camera,
      controls: { target: THREE.Vector3; update?: () => void } | null
    ) => {
      sceneRef.current = scene
      cameraRef.current = camera
      controlsRef.current = controls
    },
    []
  )

  const handleCameraPositionChange = useCallback(
    (v: [number, number, number]) => {
      const cam = cameraRef.current
      if (!cam) return
      cam.position.set(...v)
      cam.updateMatrixWorld(true)
      controlsRef.current?.update?.()
    },
    []
  )

  const handleCameraTargetChange = useCallback(
    (v: [number, number, number]) => {
      const ctrl = controlsRef.current
      if (ctrl) {
        ctrl.target.set(...v)
        ctrl.update?.()
      } else {
        const cam = cameraRef.current
        if (cam) cam.lookAt(...v)
      }
    },
    []
  )

  const handleMutated = useCallback(() => {
    const scene = sceneRef.current
    if (!scene) return
    setObjects(collectSceneObjects(scene))
    setMutationTick((t) => t + 1)
  }, [])

  const handleAddLight = useCallback(
    (type: AddableLightType) => {
      const scene = sceneRef.current
      if (!scene) return
      const light = createLight(type)
      light.name = `${type}_debug_${Math.floor(Math.random() * 1000)}`
      light.userData[DEBUG_USER_DATA_KEY] = true
      scene.add(light)
      if (objectHasTarget(light)) scene.add(light.target)
      setObjects(collectSceneObjects(scene))
      setSelectedUuid(light.uuid)
      setMutationTick((t) => t + 1)
    },
    []
  )

  const handleLogLights = useCallback(() => {
    const scene = sceneRef.current
    if (!scene) return
    logFullConfig(scene, cameraRef.current, controlsRef.current?.target ?? null)
  }, [])

  const handleRemove = useCallback((uuid: string) => {
    const scene = sceneRef.current
    if (!scene) return
    const obj = scene.getObjectByProperty('uuid', uuid)
    if (!obj) return
    if (objectHasTarget(obj) && obj.target.parent) {
      obj.target.parent.remove(obj.target)
    }
    obj.parent?.remove(obj)
    if (isLight(obj)) obj.dispose?.()
    setObjects(collectSceneObjects(scene))
    setSelectedUuid((cur) => (cur === uuid ? null : cur))
    setMutationTick((t) => t + 1)
  }, [])

  // Touch mutationTick to silence unused warning; consumers can read it via
  // the re-render it triggers.
  void mutationTick

  const canvasNode = enabled ? (
    <SceneDebugCollector
      onCollect={setObjects}
      onCameraSnapshot={setCameraInfo}
      onFpsSample={setFpsInfo}
      selectedUuid={selectedUuid}
      onSceneReady={handleSceneReady}
      intervalMs={intervalMs}
    />
  ) : null

  const overlayNode = enabled ? (
    <SceneDebugOverlay
      objects={objects}
      selectedUuid={selectedUuid}
      onSelect={setSelectedUuid}
      sceneRef={sceneRef}
      onMutated={handleMutated}
      onAddLight={handleAddLight}
      onRemove={handleRemove}
      onLogLights={handleLogLights}
      cameraInfo={cameraInfo}
      fpsInfo={fpsInfo}
      onCameraPositionChange={handleCameraPositionChange}
      onCameraTargetChange={handleCameraTargetChange}
    />
  ) : null

  return { enabled, objects, selectedUuid, canvasNode, overlayNode }
}
